import {
  COOKIE_KEY_PREFERRED_COUNTRY,
  COOKIE_KEY_PREFERRED_LANGUAGE,
} from '@application/constants';
import { i18n } from '@application/i18n';
import { cookies, useModal, useRouter } from '@application/utils';
import type { SelectPreferredCountryLanguageFormValues } from '@presentation/components';
import { SelectPreferredCountryLanguageModal } from '@presentation/components';
import { HomeLandingPage } from '@presentation/pages';
import { FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { SeoController } from '../../SeoController';

export const SelectPreferredCountryLanguagePageController: FC = () => {
  const { modal } = useModal({ open: true });
  const navigate = useNavigate();
  const { translate } = useRouter();

  const languageFromNavigator =
    typeof window !== 'undefined' ? new Intl.Locale(navigator.language).language : undefined;

  const initialLanguageFromNavigator = useMemo(() => {
    switch (languageFromNavigator) {
      case 'fr':
        return 'be/fr';
      case 'en':
        return 'eu/en';
      case 'de':
        return 'de/de';
      default:
        return 'be/nl';
    }
  }, [languageFromNavigator]);

  const initialModalLanguage = useMemo(() => {
    switch (languageFromNavigator) {
      case 'nl':
        return 'nl';
      case 'de':
        return 'de';
      case 'en':
        return 'en';
      default:
        return 'fr';
    }
  }, [languageFromNavigator]);

  const form = useFormik<SelectPreferredCountryLanguageFormValues>({
    initialValues: {
      country: initialLanguageFromNavigator,
    },
    onSubmit: async (values) => {
      const [country, language] = values.country.split('/');
      await i18n.reloadResources([language]);

      cookies.set(COOKIE_KEY_PREFERRED_LANGUAGE, language, { path: '/' });
      cookies.set(COOKIE_KEY_PREFERRED_COUNTRY, country, { path: '/' });

      navigate({ pathname: `/${country}/${language}/` });
    },
  });

  return (
    <>
      <SeoController
        noIndex
        title={translate('landing.seo.title')}
        description={translate('landing.seo.description')}
      />
      <HomeLandingPage />

      <FormikProvider value={form}>
        <SelectPreferredCountryLanguageModal modal={modal} language={initialModalLanguage} />
      </FormikProvider>
    </>
  );
};
